import ReactDOM from 'react-dom';
import { useState, useEffect, ReactNode } from 'react';
// components
import Background from './Background';
import Sheet from './Sheet';
// styles
import { mediaValue } from '../styles';

interface Props {
  closeCallback?: Function;
  children: ReactNode;
}

/**
 * Bottom Sheet
 * - closeCallback: Sheet가 완전히 닫혔을 때, 콜백함수
 *
 * @example
 * // 앱 최상단에 아래와 같이 Portal 위치지정자 태그가 필요합니다.
 * <div id="cds-bottomsheet" />
 *
 * // 다음과 같이 사용해주세요.
 * const Example = () => {
 *   const [show, setShow] = useState(false);
 *
 *   const onToggle = () => setShow(v => !v);
 *
 *   return (
 *     <>
 *       <button onClick={onToggle}>BottomSheet 열기</button>
 *       {show && (
 *         <BottomSheet closeCallback={onToggle}>
 *           <h1>메뉴</h1>
 *           <p>옵션1</p>
 *           <p>옵션2</p>
 *           <p>옵션3</p>
 *         </BottomSheet>
 *       )}
 *     </>
 *   );
 * };
 */
const BottomSheet = ({ closeCallback, children }: Props) => {
  /** Bottom sheet 애니메이션 duration 시간 */
  const ANIME_DURATION = 200;

  const [show, setShow] = useState(typeof window !== 'undefined');
  const [animated, setAnimated] = useState<SheetAnimated>('OPEN');

  const onCloseWithAnime = e => {
    e.preventDefault();
    setAnimated('CLOSE');
  };

  const onClose = () => {
    setShow(false);
    closeCallback && closeCallback();
  };

  /** Bottomsheet on/off 여부 */
  useEffect(() => {
    let timer = null;

    if (animated === 'OPEN') {
      timer = window.setTimeout(() => setAnimated('NONE'), ANIME_DURATION);
    } else if (animated === 'CLOSE') {
      timer = window.setTimeout(() => {
        setShow(false);
        closeCallback && closeCallback();
      }, ANIME_DURATION);
    }

    return () => window.clearTimeout(timer);
  }, [animated, closeCallback]);

  useEffect(() => {
    const enableDocumentScrolled = () => {
      document.querySelector('html').removeAttribute('style');
      document.querySelector('body').removeAttribute('style');
    };

    const disableDocumentScrolled = () => {
      document.querySelector('html').style.position = 'relative';
      document.querySelector('html').style.overflowY = 'hidden';
      document.querySelector('body').style.position = 'relative';
    };

    if (show && window.innerWidth < mediaValue.large) {
      disableDocumentScrolled();
    } else {
      enableDocumentScrolled();
    }
    return () => enableDocumentScrolled();
  }, [show]);

  return (
    <>
      {show && (
        <Portal>
          <Background animated={animated} onMouseDown={onCloseWithAnime}>
            <Sheet animated={animated} duration={ANIME_DURATION} onClose={onClose}>
              {children}
            </Sheet>
          </Background>
        </Portal>
      )}
    </>
  );
};

const Portal = ({ children }) => {
  const destination = document.getElementById('cds-bottomsheet');
  return ReactDOM.createPortal(children, destination);
};

type SheetAnimated = 'OPEN' | 'CLOSE' | 'NONE';

export default BottomSheet;
